import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../config/azure.config";
import { baseUrlRes1 } from "../../Restore/constants/baseUrl";

const LOGIN_TIMEOUT_MS = 45000;

export class AzureSSOError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "AzureSSOError";
    this.code = code;
  }
}

class AzureSSOService {
  constructor() {
    this.msalInstance = null;
    this.initialized = false;
    this.lastAuthResult = null;
  }

  async initializeMSAL() {
    try {
      if (!this.msalInstance) {
        this.msalInstance = new PublicClientApplication(msalConfig);
      }

      if (!this.initialized) {
        await this.msalInstance.initialize();
        this.initialized = true;
      }
    } catch (error) {
      console.error("Failed to initialize MSAL:", error);
      throw error;
    }
  }

  mapMsalError(error) {
    const code = String(error?.errorCode || error?.code || "").toLowerCase();
    const message = String(error?.message || error?.errorMessage || "").toLowerCase();

    if (
      code.includes("popup_window") ||
      code.includes("empty_window") ||
      message.includes("popup") && message.includes("blocked")
    ) {
      return new AzureSSOError(
        "popup_blocked",
        "Sign-in window was blocked. Allow popups for this site, or continue in this window."
      );
    }

    if (
      code.includes("user_cancelled") ||
      code.includes("user_canceled") ||
      message.includes("user cancelled") ||
      message.includes("user canceled")
    ) {
      return new AzureSSOError("cancelled", "Sign-in was cancelled.");
    }

    if (code === "timeout" || message.includes("timed out")) {
      return new AzureSSOError("timeout", "Sign-in timed out. Please try again.");
    }

    return new AzureSSOError(
      code || "sso_failed",
      error?.message || error?.errorMessage || "Azure SSO login failed. Please try again."
    );
  }

  withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        setTimeout(() => {
          reject(new AzureSSOError("timeout", "Sign-in timed out. Please try again."));
        }, ms);
      }),
    ]);
  }

  extractIdentity(authResult) {
    const account = authResult?.account || {};
    const claims = authResult?.idTokenClaims || {};
    const email =
      claims.preferred_username ||
      claims.email ||
      claims.upn ||
      account.username ||
      null;

    return {
      account,
      email,
      idToken: authResult.idToken,
      accessToken: authResult.accessToken,
      claims,
    };
  }

  async loginWithAzure(serviceId = null) {
    try {
      await this.initializeMSAL();

      // Always prompt for credentials — never reuse a cached account silently.
      const authResult = await this.withTimeout(
        this.msalInstance.loginPopup({
          ...loginRequest,
          prompt: "login",
        }),
        LOGIN_TIMEOUT_MS
      );

      this.lastAuthResult = authResult;
      return await this.completeLoginFromAuthResult(authResult, serviceId);
    } catch (error) {
      console.error("Azure SSO login failed:", error);
      throw this.mapMsalError(error);
    }
  }

  async loginWithRedirect(serviceId = null) {
    await this.initializeMSAL();
    if (serviceId != null) {
      sessionStorage.setItem("azure_sso_pending_service_id", String(serviceId));
    }
    await this.msalInstance.loginRedirect({
      ...loginRequest,
      prompt: "login",
    });
  }

  async handleRedirectResult() {
    await this.initializeMSAL();
    const authResult = await this.msalInstance.handleRedirectPromise();
    if (!authResult) {
      return null;
    }

    this.lastAuthResult = authResult;
    const pendingServiceId = sessionStorage.getItem("azure_sso_pending_service_id");
    sessionStorage.removeItem("azure_sso_pending_service_id");
    const serviceId = pendingServiceId ? Number(pendingServiceId) : null;
    return await this.completeLoginFromAuthResult(authResult, serviceId);
  }

  async completeLoginFromAuthResult(authResult, serviceId = null) {
    const identity = this.extractIdentity(authResult);
    if (!identity.idToken) {
      throw new AzureSSOError("missing_token", "No ID token returned from Microsoft.");
    }

    const loginResponse = await this.authenticateWithBackend(identity, serviceId);
    return {
      success: true,
      data: loginResponse,
      userInfo: {
        email: loginResponse.email || identity.email,
        userPrincipalName: identity.email,
        displayName: identity.claims.name || identity.account.name,
        givenName: identity.claims.given_name,
        surname: identity.claims.family_name,
      },
    };
  }

  async requestAccessWithService(serviceId) {
    if (!this.lastAuthResult?.idToken) {
      throw new AzureSSOError(
        "missing_session",
        "Microsoft sign-in expired. Please click Sign in with Work Email again."
      );
    }
    return await this.completeLoginFromAuthResult(this.lastAuthResult, serviceId);
  }

  async authenticateWithBackend(identity, serviceId = null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    try {
      const body = {
        azureToken: identity.idToken,
        accessToken: identity.accessToken || null,
        account: {
          name: identity.account.name,
          username: identity.account.username,
          homeAccountId: identity.account.homeAccountId,
          localAccountId: identity.account.localAccountId,
          environment: identity.account.environment,
          tenantId: identity.account.tenantId,
        },
      };

      if (serviceId != null && !Number.isNaN(Number(serviceId))) {
        body.serviceId = Number(serviceId);
      }

      const response = await fetch(`${baseUrlRes1}api/v1/azure-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      const loginData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new AzureSSOError(
          "backend_auth_failed",
          loginData.message || "Backend authentication failed"
        );
      }

      return loginData;
    } catch (error) {
      if (error?.name === "AbortError") {
        throw new AzureSSOError("timeout", "Sign-in timed out. Please try again.");
      }
      if (error instanceof AzureSSOError) {
        throw error;
      }
      console.error("Error authenticating with backend:", error);
      throw new AzureSSOError(
        "backend_auth_failed",
        error.message || "Backend authentication failed"
      );
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async clearLocalSession() {
    try {
      await this.initializeMSAL();
      this.lastAuthResult = null;
      if (this.msalInstance) {
        this.msalInstance.setActiveAccount(null);
        if (typeof this.msalInstance.clearCache === "function") {
          await this.msalInstance.clearCache();
        }
      }
    } catch (error) {
      console.error("Azure local session clear failed:", error);
    }
  }

  async logout() {
    try {
      await this.clearLocalSession();
    } catch (error) {
      console.error("Azure logout failed:", error);
    }
  }

  isAzureConfigured() {
    return !!(
      import.meta.env.VITE_AZURE_CLIENT_ID && import.meta.env.VITE_AZURE_TENANT_ID
    );
  }
}

const azureSSOService = new AzureSSOService();
export default azureSSOService;
